import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  // Dual-role: Track whether user is currently viewing as host or guest
  const [activeMode, setActiveMode] = useState(localStorage.getItem('activeMode') || 'guest');

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Sync activeMode to localStorage
  useEffect(() => {
    localStorage.setItem('activeMode', activeMode);
  }, [activeMode]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      // If user is a host and was in host mode, keep it; otherwise default to guest
      if (response.data.is_host && localStorage.getItem('activeMode') === 'host') {
        setActiveMode('host');
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    setToken(response.data.token);
    setUser(response.data.user);
    localStorage.setItem('token', response.data.token);
    // Default to guest mode on login, unless they're a host
    if (response.data.user.is_host) {
      setActiveMode('host');
    } else {
      setActiveMode('guest');
    }
    return response.data;
  };

  const signup = async (email, password, name, role, referralCode = null, displayName = null, dateOfBirth = null, state = null) => {
    const payload = { email, password, name, role };
    if (referralCode) {
      payload.referral_code = referralCode;
    }
    if (displayName) {
      payload.display_name = displayName;
    }
    if (dateOfBirth) {
      payload.date_of_birth = dateOfBirth;
    }
    if (state) {
      payload.state = state;
    }
    payload.terms_accepted = true;  // User accepted terms before calling signup
    const response = await axios.post(`${API_URL}/api/auth/signup`, payload);
    setToken(response.data.token);
    setUser(response.data.user);
    localStorage.setItem('token', response.data.token);
    setActiveMode('guest');
    return response.data;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setActiveMode('guest');
    localStorage.removeItem('token');
    localStorage.removeItem('activeMode');
  };

  const switchMode = (mode) => {
    if (mode === 'host' && !user?.is_host) {
      // Can't switch to host mode if not a host yet
      return false;
    }
    setActiveMode(mode);
    return true;
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  const becomeHost = async (hostData) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/become-host`, hostData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      setActiveMode('host');
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      login, 
      signup, 
      logout, 
      loading,
      activeMode,
      switchMode,
      updateUser,
      becomeHost
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};